import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { apiRequest } from "../../../core/common/api";
import {
  AdminPermissionKey,
  canManageAdmins,
} from "../../../core/common/adminPermissions";
import {
  AdminAccountFields,
  AdminFormState,
  PermissionPicker,
  createEmptyAdminForm,
} from "./adminFormShared";

const AdminAdd = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<AdminFormState>(createEmptyAdminForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!canManageAdmins()) {
      navigate("/admins", { replace: true });
    }
  }, [navigate]);

  const updateForm = (updates: Partial<AdminFormState>) => {
    setForm((current) => ({ ...current, ...updates }));
  };

  const togglePermission = (key: AdminPermissionKey) => {
    setForm((current) => ({
      ...current,
      permissions: current.permissions.map((item) =>
        item.name === key ? { ...item, isActive: !item.isActive } : item,
      ),
    }));
  };

  const setAllPermissions = (enabled: boolean) => {
    setForm((current) => ({
      ...current,
      permissions: current.permissions.map((item) => ({ ...item, isActive: enabled })),
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.permissions.some((item) => item.isActive)) {
      Swal.fire("Validation Error", "Select at least one module permission", "error");
      return;
    }

    setSaving(true);

    try {
      const result = await apiRequest("/admins", "POST", {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password.trim(),
        role: form.role,
        isActive: form.isActive,
        permissions: form.permissions,
      });

      if (!result.success) throw new Error(result.message || "Unable to create admin");

      await Swal.fire({
        title: "Admin Created",
        text: "Account and permissions saved successfully.",
        icon: "success",
        timer: 1400,
        showConfirmButton: false,
      });

      navigate(result.admin?._id ? `/admins/${result.admin._id}` : "/admins");
    } catch (error: any) {
      Swal.fire("Create Failed", error.message || "Unable to create admin", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="detail-page">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <Link to="/admins" className="btn-sm btn-outline back-link">
            ← Back to All Admins
          </Link>
          <div className="page-eyebrow">Access Control</div>
          <h1 className="page-title">
            Add New <em>Admin</em>
          </h1>
          <div className="page-sub">
            Create a manager or support account and assign module permissions.
          </div>
        </div>
        <div className="page-hdr-right">
          <button type="submit" form="admin-add-form" className="btn-sm btn-primary" disabled={saving}>
            {saving ? "Creating..." : "Create Admin"}
          </button>
        </div>
      </div>

      <form id="admin-add-form" onSubmit={handleSubmit} className="page-stack">
        <section className="table-card">
          <div className="table-head">
            <h2>Account Details</h2>
            <p>Basic login information and role assignment</p>
          </div>
          <div className="admin-card-body">
            <AdminAccountFields form={form} onChange={updateForm} />
          </div>
        </section>

        <section className="table-card">
          <div className="table-head">
            <h2>Panel Permissions</h2>
            <p>Choose which modules this admin can access</p>
          </div>
          <div className="admin-card-body">
            <PermissionPicker
              permissions={form.permissions}
              onToggle={togglePermission}
              onSetAll={setAllPermissions}
            />
          </div>
        </section>

        <div className="detail-footer">
          <Link to="/admins" className="btn-sm btn-outline">
            Cancel
          </Link>
          <div className="detail-footer-right">
            <button type="submit" className="btn-sm btn-primary" disabled={saving}>
              {saving ? "Creating..." : "Create Admin"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdminAdd;
